import { useCallback, useMemo } from "react";

import { useHistory } from "react-router-dom";
import {
  AnalyticsDataSource,
  EarlyAccessFeature,
  Roles,
  type RunReportChannelStatus,
  SystemLabel,
} from "@doitintl/cmp-models";

import { useAuthContext } from "../../../Context/AuthContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { useIsFeatureEntitled } from "../../../Context/TierProvider";
import { type ReportWSnap } from "../../../types";
import { consoleErrorWithSentry } from "../../../utils";
import mixpanel from "../../../utils/mixpanel";
import { useQuery } from "../../../utils/useQuery";
import { useCloudAnalyticsContext } from "../CloudAnalyticsContext";
import useGenerateReport from "../generateReport";
import { type PayloadConfig } from "../generateReport/types";
import { useLabels } from "../labels/hooks";
import { isSystemLabel } from "../labels/utils";
import { getOrganizationRef } from "../utilities";
import { createRunReportChannel } from "./db";

export const useNewReportHandler = ({
  baseUrl,
  mixpanelEventName,
  additionalState = {},
  replaceCurrentRoute = false,
}: {
  baseUrl: string;
  mixpanelEventName: string;
  additionalState?: object;
  replaceCurrentRoute?: boolean;
}) => {
  const { customer, userOrganization, init: customerCtxInit } = useCustomerContext();
  const { currentUser } = useAuthContext({ mustHaveUser: true });
  const { isDoitEmployee } = useAuthContext({ mustHaveUser: true });
  const { defaultReportConfig } = useCloudAnalyticsContext();
  const history = useHistory();
  const generateReport = useGenerateReport();
  const query = useQuery();

  return useCallback(async () => {
    if (customerCtxInit && currentUser.email) {
      const orgRef = getOrganizationRef(isDoitEmployee, userOrganization, customer.id);
      try {
        const newReportId = await generateReport(
          {
            description: "",
            customer: customer.ref,
            organization: orgRef,
            name: "",
            collaborators: [{ email: currentUser.email, role: Roles.OWNER }],
            config: defaultReportConfig as PayloadConfig,
            draft: true,
          },
          true
        );
        mixpanel.track(mixpanelEventName, { reportId: newReportId, draft: true });
        const reportUrl = `${baseUrl}/${newReportId}`;
        const reportUrlWithQuery = query.size > 0 ? `${reportUrl}?${query.toString()}` : reportUrl;

        setTimeout(() => {
          if (replaceCurrentRoute) {
            history.replace(reportUrlWithQuery, {
              ...additionalState,
              new: true,
            });
          } else {
            history.push(reportUrlWithQuery, {
              ...additionalState,
              new: true,
            });
          }
        }, 0);
      } catch (error) {
        consoleErrorWithSentry(error);
      }
    }
  }, [
    customerCtxInit,
    currentUser.email,
    isDoitEmployee,
    userOrganization,
    customer.id,
    customer.ref,
    generateReport,
    defaultReportConfig,
    mixpanelEventName,
    baseUrl,
    query,
    replaceCurrentRoute,
    history,
    additionalState,
  ]);
};

export const useCreateRunReportChannel = ({ status }: { status: RunReportChannelStatus }) => {
  const { currentUser } = useAuthContext({ mustHaveUser: true });

  return useCallback(
    async () =>
      createRunReportChannel({
        uid: currentUser.uid,
        email: currentUser.email,
        status,
      }),
    [currentUser.email, currentUser.uid, status]
  );
};

export const useReportSystemLabels = (report: ReportWSnap | null) => {
  const [labels] = useLabels();

  const isScheduled = useMemo(() => {
    if (!report?.data.labels || !labels) return false;
    const scheduledLabel = labels.find((label) =>
      report.data.labels?.some(
        (reportLabel) =>
          isSystemLabel(label.data?.type ?? "") &&
          label.data.name === SystemLabel.Scheduled &&
          label.ref.id === reportLabel.id
      )
    );

    return !!scheduledLabel;
  }, [labels, report?.data.labels]);

  const isCached = useMemo(() => {
    if (!report?.data.labels || !labels) return false;
    const cachedLabel = labels.find((label) =>
      report.data.labels?.some(
        (reportLabel) =>
          isSystemLabel(label.data?.type ?? "") &&
          label.data.name === SystemLabel.Cached &&
          label.ref.id === reportLabel.id
      )
    );

    return !!cachedLabel;
  }, [labels, report?.data.labels]);

  return {
    isScheduled,
    isCached,
  };
};

export const useIsCustomerEntitledToDatasource = (datasource: AnalyticsDataSource) => {
  const isEntitledToK8s = useIsFeatureEntitled("lens:k8s");

  if (datasource === AnalyticsDataSource.KUBERNETES_UTILIZATION) {
    return isEntitledToK8s ?? false;
  }

  return true;
};

export const useIsCustomerEntitledToCloudZero = () => {
  const { customer } = useCustomerContext();

  return (
    useIsFeatureEntitled("cloudzero:connect") && customer.earlyAccessFeatures?.includes(EarlyAccessFeature.CLOUD_ZERO)
  );
};
